import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import { runTask } from 'ember-lifeline';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { toDate as toDateFnsTz } from 'date-fns-tz';
import { OccasionRecord } from 'uplisting-frontend/services/calendar/-internals/record';
import { occupantsOf } from 'uplisting-frontend/services/calendar/-internals/occupancy';
import { canonicalDay } from 'uplisting-frontend/services/calendar/-internals/utils';
import type {
  LocationType,
  PropertyRowDataItem,
} from 'uplisting-frontend/services/calendar/-internals/types';
import type { IUnitMove } from 'uplisting-frontend/services/calendar/unit-move';
import { dateUtcAligned, formatLocalDate } from 'uplisting-frontend/utils/date';
import { formatDate } from 'uplisting-frontend/helpers/date-format';
import BookingModel from 'uplisting-frontend/models/booking';
import PropertyModel from 'uplisting-frontend/models/property';
import UnavailabilityModel from 'uplisting-frontend/models/unavailability';
import BookingRequestModel from 'uplisting-frontend/models/booking-request';
import EnquiryModel from 'uplisting-frontend/models/enquiry';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { animatedTransition } from 'uplisting-frontend/utils/router';

export interface IDragOperation {
  location: LocationType;
  record: OccasionRecord;
  fromDate?: Date;
  toDate?: Date;
  isValid: boolean;
  finished: boolean;
  originalMultiUnitId?: string;
  originalMultiUnit?: MultiUnitModel;
  originalCheckIn?: string;
  originalCheckOut?: string;
  anchored?: boolean;
  grabFloat?: number;
  // the drop is allowed but overlaps existing records on the target unit,
  // so completing it creates a conflict the user resolves afterwards
  createsConflict?: boolean;
}

export interface IResizeOperation {
  location: LocationType;
  record: OccasionRecord;
  fromDate?: Date;
  toDate?: Date;
  doesNotIntersect?: boolean;
  isValid: boolean;
  finished: boolean;
  resizeType?: 'start' | 'end';
}

export interface IEventOperation {
  location: LocationType;
  fromDate: Date;
  toDate: Date;
  isValid: boolean;
  finished: boolean;
}

export type SpotRangeKind =
  | 'mlos'
  | 'closedForArrival'
  | 'closedForDeparture'
  | 'rates';

export interface ISpotRangeOperation {
  property: PropertyModel;
  kind: SpotRangeKind;
  currentValue: string;
  fromDate: Date;
  toDate: Date;
  finished: boolean;
}

const SPOT_RANGE_ROUTE: Record<SpotRangeKind, string> = {
  mlos: 'properties.spot-mlos',
  closedForArrival: 'properties.spot-arrival',
  closedForDeparture: 'properties.spot-departure',
  rates: 'properties.spot-rates',
};

const SPOT_RANGE_VALUE_QUERY_PARAM: Record<SpotRangeKind, string> = {
  mlos: 'length',
  closedForArrival: 'closedForArrival',
  closedForDeparture: 'closedForDeparture',
  rates: 'rate',
};

// A booking belongs to a property whether its row is the property itself or one
// of its units, so this is what "the same property" means for a drag.
export function owningProperty(location: LocationType): LocationType {
  return location instanceof MultiUnitModel ? location.property : location;
}

// A multi-unit property's own row aggregates every unit's bookings, so it is a
// view rather than a place a unit's booking can land — dropping there cannot say
// which unit it belongs to. Keep a drag on rows of the record's own kind: unit
// bookings between units, and a booking with no unit on the property row it
// already occupies.
export function sameRowKind(a: LocationType, b: LocationType): boolean {
  return a instanceof MultiUnitModel === b instanceof MultiUnitModel;
}

export default class CalendarInteractionService extends Service {
  @service router!: Services['router'];
  @service session!: Services['session'];
  @service notifications!: Services['notifications'];
  @service('calendar/data') calendarData!: Services['calendar/data'];
  @service('calendar/unit-move') unitMove!: Services['calendar/unit-move'];
  @service('booking-drawer') bookingDrawer!: Services['booking-drawer'];

  @service('calendar/conflict-resolution')
  resolution!: Services['calendar/conflict-resolution'];

  // set on mousedown, so the drop preview can show on press rather than waiting
  // for dragstart (which the browser defers until the pointer has moved)
  @tracked pressedRecord?: OccasionRecord;

  @tracked dragOperation?: IDragOperation;
  @tracked eventOperation?: IEventOperation;
  @tracked resizeOperation?: IResizeOperation;
  @tracked spotRangeOperation?: ISpotRangeOperation;

  // restored when a press is dropped, as an open panel may still read it
  #eventBeforePress?: IEventOperation;
  #spotRangeBeforePress?: ISpotRangeOperation;

  constructor(owner: Owner) {
    super(owner);

    registerDestructor(this, () => {
      window.removeEventListener('mouseup', this.#dropUnfinishedSelection, {
        capture: true,
      });
    });
  }

  @cached
  get isCalendarRoute(): boolean {
    return (this.router.currentRouteName ?? '').startsWith('calendar.');
  }

  @cached
  get isXCalendarRoute(): boolean {
    return (this.router.currentRouteName ?? '').startsWith('xcalendar.');
  }

  get dragChangedMultiUnit(): boolean {
    const { dragOperation } = this;

    return (
      dragOperation?.finished === true &&
      dragOperation.location instanceof MultiUnitModel &&
      dragOperation.location.id !== dragOperation.originalMultiUnitId
    );
  }

  get dragChangedDates(): boolean {
    const { dragOperation } = this;

    if (!dragOperation?.finished) {
      return false;
    }

    const { model } = dragOperation.record;

    return (
      model.checkIn !== dragOperation.originalCheckIn ||
      model.checkOut !== dragOperation.originalCheckOut
    );
  }

  // drag section
  public startDrag(
    location: LocationType,
    record: OccasionRecord,
    anchored = false,
  ): void {
    if (!record.meta.isDraggable) {
      return;
    }

    const model = record.model as ModelRegistry['booking'];

    if (!this.resolution.canMove(model)) {
      return;
    }

    this.dragOperation = {
      location,
      record,
      isValid: false,
      finished: false,
      originalMultiUnitId: model.multiUnit?.id,
      originalMultiUnit: model.multiUnit,
      originalCheckIn: model.checkIn,
      originalCheckOut: model.checkOut,
      anchored,
    };
  }

  public updateDrag(
    toDate: Date,
    location: LocationType,
    fraction?: number,
  ): void {
    const { dragOperation } = this;

    if (!dragOperation || !dragOperation.record.meta.isDraggable) {
      return;
    }

    // A drag never leaves its property, nor moves between a property row and a
    // unit row. Ignore the update rather than rebuilding the operation onto a row
    // it can never land on: that left the drop indicator stranded in the other
    // row, and the reset/rebuild churn re-rendered the source chip mid-drag,
    // losing the dragend that would have cleared it.
    if (
      owningProperty(dragOperation.location) !== owningProperty(location) ||
      !sameRowKind(dragOperation.location, location)
    ) {
      return;
    }

    const { record } = dragOperation;

    if (
      record.model instanceof UnavailabilityModel &&
      location !== dragOperation.location
    ) {
      return;
    }

    let effectiveStart = toDate;
    let { grabFloat } = dragOperation;

    if (this.#holdsDates(record.model)) {
      effectiveStart = canonicalDay(record.meta.startDate);
    } else if (dragOperation.anchored) {
      // calendar days, not elapsed ones, which lose a day left of the start
      const currentFloat =
        differenceInCalendarDays(toDate, record.meta.startDate) +
        (fraction ?? 0);

      // the first update records the grab point, so the bar stays put
      grabFloat ??= currentFloat;

      // canonical, like the cell dates the preview and its dedupe compare with
      effectiveStart = canonicalDay(
        addDays(record.meta.startDate, Math.round(currentFloat - grabFloat)),
      );
    }

    if (
      dragOperation.toDate?.getTime() === effectiveStart.getTime() &&
      dragOperation.location === location
    ) {
      return;
    }

    const newEndDate = addDays(effectiveStart, record.meta.duration);

    const overlaps =
      !this.resolution.isActive &&
      !this.#dateDoesNotIntersectExcluding(
        effectiveStart,
        newEndDate,
        location,
        record,
      );

    // hosts intentionally double-occupy units of a multi-unit while
    // rearranging guests, so overlapping drops onto a unit are allowed and
    // surface as a conflict to resolve; single-property rows keep the
    // rejection, as a double-booking there has no unit to shuffle out to
    const isValid =
      !overlaps ||
      (location instanceof MultiUnitModel &&
        record.model instanceof BookingModel);

    this.dragOperation = {
      ...dragOperation,

      grabFloat,
      toDate: effectiveStart,
      location,
      isValid,
      createsConflict: overlaps && isValid,
    };
  }

  public async completeDrag(): Promise<void> {
    const { dragOperation } = this;

    this.pressedRecord = undefined;

    if (!dragOperation) {
      return;
    }

    if (this.resolution.isActive) {
      this.#placeForResolution(dragOperation);

      return;
    }

    // dropped back where it started: nothing to persist, so cancel quietly rather
    // than routing to the booking — and, for a bar that already overlaps a
    // neighbour, rather than warning that the dates are unavailable
    if (this.#dragChangesNothing(dragOperation)) {
      this.resetDragOperation();

      return;
    }

    if (!dragOperation.isValid) {
      if (dragOperation.toDate) {
        this.notifications.error(
          dragOperation.record.model instanceof UnavailabilityModel
            ? 'calendar.notifications.block_move_dates_unavailable'
            : 'calendar.notifications.move_dates_unavailable',
        );
      }

      this.resetDragOperation();

      return;
    }

    const swap = this.#swapForDrop(dragOperation);

    if (swap) {
      this.dragOperation = undefined;

      if (!(await this.unitMove.confirm(swap))) {
        return;
      }
    }

    // reassign, not mutate: the drag UI reads `finished`, and setting a property
    // on the tracked object would not invalidate it
    this.dragOperation = { ...dragOperation, finished: true };

    const { record } = dragOperation;
    const { model } = record;

    const fromDate = dragOperation.toDate as Date;

    // before the dates change, as the duration is read from them
    const newCheckOut = addDays(fromDate, record.meta.duration);

    model.checkIn = formatLocalDate(fromDate);
    model.checkOut = formatLocalDate(newCheckOut);

    if (
      model instanceof BookingModel &&
      dragOperation.location instanceof MultiUnitModel
    ) {
      model.multiUnit = dragOperation.location;
    }

    this.calendarData.setupFasterLookup();

    if (dragOperation.createsConflict) {
      this.notifications.info('calendar.notifications.move_created_conflict');
    }

    await this.#showMoved(record);
  }

  // new event section
  public startEvent(location: LocationType, from: Date): void {
    if (
      !this.session.currentUser?.hasAccessTo('calendar.event') ||
      this.resolution.keepsArrangement()
    ) {
      return;
    }

    this.#eventBeforePress = this.eventOperation?.finished
      ? this.eventOperation
      : undefined;

    this.eventOperation = {
      location,
      fromDate: from,
      toDate: from,
      finished: false,
      isValid: true,
    };

    this.#watchRelease();
  }

  public updateEvent(newDate: Date, validate = true): void {
    const { eventOperation } = this;

    if (!eventOperation) {
      return;
    }

    const { fromDate } = eventOperation;

    const isValid = validate
      ? this.dateDoesNotIntersect(fromDate, newDate, eventOperation.location) &&
        this.dateDoesNotIntersect(newDate, fromDate, eventOperation.location)
      : true;

    this.eventOperation = {
      ...eventOperation,

      toDate: newDate,
      isValid,
    };
  }

  async completeEvent(validate = true): Promise<void> {
    if (!this.eventOperation) {
      return;
    }

    this.#updateEventBeforeComplete(validate);

    if (!this.eventOperation.isValid) {
      this.resetEventOperation();

      return;
    }

    this.eventOperation.finished = true;

    const { location } = this.eventOperation;

    const start = `${this.eventOperation.fromDate.getTime()}`;
    const end = `${(this.eventOperation.toDate as Date).getTime()}`;

    this.#openEvent(location, start, end);
  }

  public createCompletedEventOperation(
    location: LocationType,
    start: string,
    end: string,
  ): void {
    this.eventOperation = {
      location,
      fromDate: toDateFnsTz(Number(start)),
      toDate: toDateFnsTz(Number(end)),
      isValid: true,
      finished: true,
    };
  }

  // spot range section
  public startSpotRange(
    property: PropertyModel,
    kind: SpotRangeKind,
    currentValue: string,
    from: Date,
  ): void {
    this.#spotRangeBeforePress = this.spotRangeOperation?.finished
      ? this.spotRangeOperation
      : undefined;

    this.spotRangeOperation = {
      property,
      kind,
      currentValue,
      fromDate: from,
      toDate: from,
      finished: false,
    };

    this.#watchRelease();
  }

  public updateSpotRange(newDate: Date): void {
    const { spotRangeOperation } = this;

    if (!spotRangeOperation || spotRangeOperation.finished) {
      return;
    }

    this.spotRangeOperation = {
      ...spotRangeOperation,
      toDate: newDate,
    };
  }

  public completeSpotRange(): void {
    const { spotRangeOperation } = this;

    if (!spotRangeOperation) {
      return;
    }

    let { fromDate, toDate } = spotRangeOperation;

    if (toDate < fromDate) {
      [fromDate, toDate] = [toDate, fromDate];
    }

    const { property, kind, currentValue } = spotRangeOperation;

    this.spotRangeOperation = {
      property,
      kind,
      currentValue,
      fromDate,
      toDate,
      finished: true,
    };

    const start = formatDate(dateUtcAligned(fromDate), 'yyyy-MM-dd');
    const end = formatDate(dateUtcAligned(addDays(toDate, 1)), 'yyyy-MM-dd');

    const route = `${this.baseRoute}.${SPOT_RANGE_ROUTE[kind]}`;
    const valueParam = SPOT_RANGE_VALUE_QUERY_PARAM[kind];

    animatedTransition(() => {
      this.router.transitionTo(route, property.id, {
        queryParams: {
          start,
          end,
          [valueParam]: currentValue,
        },
      });
    });
  }

  public resetSpotRangeOperation(): void {
    this.spotRangeOperation = undefined;
  }

  // resize section
  public canResize(record: OccasionRecord): boolean {
    return record.meta.isResizable && !this.resolution.isActive;
  }

  public startResize(
    location: LocationType,
    record: OccasionRecord,
    resizeType: 'start' | 'end',
  ): void {
    if (!this.canResize(record)) {
      return;
    }

    this.resizeOperation = {
      location,
      record,
      isValid: false,
      finished: false,
      resizeType,
      fromDate: resizeType === 'start' ? record.meta.startDate : undefined,
      toDate: resizeType === 'end' ? record.meta.endDate : undefined,
    };
  }

  public updateResize(newDate: Date): void {
    if (!this.resizeOperation) {
      return;
    }

    const { location, record, resizeType } = this.resizeOperation;

    let fromDate = record.meta.startDate;
    let toDate = record.meta.endDate;

    if (resizeType === 'start') {
      fromDate = newDate;
    } else if (resizeType === 'end') {
      toDate = newDate;
    }

    const isValidDateOrder = fromDate <= toDate;

    if (!isValidDateOrder) {
      this.resetResizeOperation();

      return;
    }

    const doesNotIntersect = this.#dateDoesNotIntersectExcluding(
      fromDate,
      toDate,
      location,
      record,
    );

    this.resizeOperation = {
      ...this.resizeOperation,
      fromDate,
      toDate,
      doesNotIntersect,
      isValid: isValidDateOrder && doesNotIntersect,
    };

    record.model.checkIn = formatLocalDate(fromDate);
    record.model.checkOut = formatLocalDate(toDate);

    this.calendarData.setupFasterLookup();
  }

  async completeResize(): Promise<void> {
    const { resizeOperation } = this;

    if (!resizeOperation) {
      return;
    }

    if (!resizeOperation.isValid) {
      this.resetResizeOperation();

      return;
    }

    resizeOperation.finished = true;

    await this.#showMoved(resizeOperation.record);
  }

  @action
  @waitFor
  async handleOccasionClick(
    record: OccasionRecord,
    event: MouseEvent,
  ): Promise<boolean> {
    event.preventDefault();
    event.stopPropagation();

    const route = this.#getRouteForRecord(record);

    if (
      !record.meta.isClickable ||
      !route ||
      this.resolution.keepsArrangement()
    ) {
      return false;
    }

    animatedTransition(() => {
      this.router.transitionTo(route, record.model.id);
    }, true);

    return true;
  }

  // reset operations
  public resetEventOperation(): void {
    this.eventOperation = undefined;
  }

  public resetDragOperation(): void {
    // cleared before the guard: a press can exist without a drag operation, and
    // it still has to be forgotten
    this.pressedRecord = undefined;

    const { dragOperation } = this;

    if (!dragOperation) {
      return;
    }

    const { model } = dragOperation.record;

    if (!this.resolution.isPlaced(model)) {
      model.rollbackAttributes();
    }

    if (
      model instanceof BookingModel &&
      dragOperation.location instanceof MultiUnitModel
    ) {
      model.multiUnit = dragOperation.originalMultiUnit;
    }

    this.calendarData.setupFasterLookup();

    this.dragOperation = undefined;
  }

  public keepDraggedUnit(): void {
    const { dragOperation } = this;

    if (!(dragOperation?.location instanceof MultiUnitModel)) {
      return;
    }

    this.dragOperation = {
      ...dragOperation,
      originalMultiUnit: dragOperation.location,
      originalMultiUnitId: dragOperation.location.id,
    };
  }

  public resetResizeOperation(): void {
    if (!this.resizeOperation) {
      return;
    }

    this.resizeOperation.record.model.rollbackAttributes();
    this.calendarData.setupFasterLookup();

    this.resizeOperation = undefined;
  }

  public clearOperations(): void {
    this.dragOperation = undefined;
    this.resizeOperation = undefined;

    this.calendarData.setupFasterLookup();
  }

  public discardMoveOf(model: PropertyRowDataItem): void {
    if (this.dragOperation?.record.model === model) {
      this.resetDragOperation();
    } else if (this.resizeOperation?.record.model === model) {
      this.resetResizeOperation();
    } else if (
      !model.isDestroyed &&
      !model.isDeleted &&
      model.hasDirtyAttributes
    ) {
      model.rollbackAttributes();
      this.calendarData.setupFasterLookup();
    }
  }

  public dateDoesNotIntersect(
    start: Date,
    end: Date,
    record: LocationType,
  ): boolean {
    return occupantsOf(record, start, end).length === 0;
  }

  get baseRoute(): string {
    if (this.isXCalendarRoute) {
      return 'xcalendar';
    } else if (this.isCalendarRoute) {
      return 'calendar';
    } else {
      return 'monthly';
    }
  }

  // an untouched drag has no `toDate` at all; one dragged away and back has a
  // `toDate` matching the check-in it started from, on the row it started on
  #dragChangesNothing({
    toDate,
    location,
    originalCheckIn,
    originalMultiUnitId,
  }: IDragOperation): boolean {
    if (!toDate) {
      return true;
    }

    const unitId = location instanceof MultiUnitModel ? location.id : undefined;

    return (
      formatLocalDate(toDate) === originalCheckIn &&
      unitId === originalMultiUnitId
    );
  }

  #holdsDates(model: PropertyRowDataItem): boolean {
    if (!(model instanceof BookingModel)) {
      return false;
    }

    const user = this.session.currentUser;

    return (
      !model.isEditable ||
      !user?.hasAccessTo('bookings.price.edit') ||
      (this.resolution.isActive && !user.hasAccessTo('calendar.bookings.price'))
    );
  }

  #placeForResolution(dragOperation: IDragOperation): void {
    const { record, location, toDate } = dragOperation;

    this.dragOperation = undefined;

    if (toDate) {
      this.resolution.place(record, location, formatLocalDate(toDate));
    }
  }

  async #showMoved(record: OccasionRecord): Promise<void> {
    const { model } = record;

    if (model instanceof UnavailabilityModel) {
      await this.router.transitionTo(
        this.#getRouteForRecord(record) as string,
        model.id,
      );
    } else {
      await this.#showMovedBooking(model as ModelRegistry['booking']);
    }
  }

  async #showMovedBooking(booking: ModelRegistry['booking']): Promise<void> {
    const segment = this.#postDragRouteSegment();

    if (this.bookingDrawer.booking === booking) {
      if (segment === 'price') {
        await this.bookingDrawer.requoteStaged();
      }

      return;
    }

    await this.router.transitionTo(
      `${this.baseRoute}.bookings.${segment}`,
      booking.id,
    );
  }

  #postDragRouteSegment(): 'price' | 'details' {
    if (this.dragChangedMultiUnit && !this.dragChangedDates) {
      return 'details';
    }

    return this.session.currentUser?.hasAccessTo('calendar.bookings.price')
      ? 'price'
      : 'details';
  }

  // captured, as a booking stops the release bubbling
  #watchRelease(): void {
    window.addEventListener('mouseup', this.#dropUnfinishedSelection, {
      capture: true,
      once: true,
    });
  }

  // deferred so the cell under the pointer can complete the selection first
  #dropUnfinishedSelection = (): void => {
    runTask(this, () => {
      if (this.eventOperation?.finished === false) {
        this.eventOperation = this.#eventBeforePress;
      }

      if (this.spotRangeOperation?.finished === false) {
        this.spotRangeOperation = this.#spotRangeBeforePress;
      }
    });
  };

  #getRouteForRecord(record: OccasionRecord): string | undefined {
    const base = this.baseRoute;

    if (record.model instanceof BookingModel) {
      return `${base}.bookings.summary`;
    } else if (record.model instanceof UnavailabilityModel) {
      return `${base}.unavailabilities`;
    } else if (record.model instanceof BookingRequestModel) {
      return `${base}.booking-requests.summary`;
    } else if (record.model instanceof EnquiryModel) {
      return `${base}.enquiries.messages`;
    }
  }

  #openEvent(location: LocationType, start: string, end: string): void {
    if (this.isXCalendarRoute) {
      this.#openOldCalendarEvent(location, start, end);
    } else if (this.isCalendarRoute) {
      this.#openCalendarEvent(location, start, end);
    } else {
      this.#openMonthlyEvent(start, end);
    }
  }

  #openCalendarEvent(location: LocationType, start: string, end: string): void {
    const isMultiUnit = location instanceof MultiUnitModel;
    const model = isMultiUnit ? location.property : location;

    const route = this.session.currentUser?.hasAccessTo(
      'calendar.createBooking',
    )
      ? `${this.baseRoute}.event`
      : `${this.baseRoute}.event.unavailability`;

    animatedTransition(() => {
      this.router.transitionTo(route, model.id, {
        queryParams: {
          start,
          end,
          multiUnitId: isMultiUnit ? location.id : undefined,
        },
      });
    });
  }

  #openOldCalendarEvent(
    location: LocationType,
    start: string,
    end: string,
  ): void {
    const isMultiUnit = location instanceof MultiUnitModel;
    const model = isMultiUnit ? location.property : location;

    const route = this.session.currentUser?.hasAccessTo(
      'calendar.createBooking',
    )
      ? `${this.baseRoute}.event.booking`
      : `${this.baseRoute}.event.unavailability`;

    animatedTransition(() => {
      this.router.transitionTo(route, model.id, {
        queryParams: {
          start,
          end,
          multiUnitId: isMultiUnit ? location.id : undefined,
        },
      });
    });
  }

  #openMonthlyEvent(start: string, end: string): void {
    const canCreateBooking = this.session.currentUser?.hasAccessTo(
      'calendar.createBooking',
    );

    const route = canCreateBooking
      ? 'monthly.event.index'
      : 'monthly.event.unavailability';

    const cb = () => {
      this.router.transitionTo(route, {
        queryParams: {
          start,
          end,
        },
      });
    };

    if (this.router.currentRouteName === route) {
      cb();
    } else {
      animatedTransition(() => {
        cb();
      });
    }
  }

  // event can go any direction, left(past) and right(future) we want to properly set dates which one is `from` and which one is `to`
  // and swap them(and not loose them) in case if `from` is after `to`, or `to` is before `from`
  #updateEventBeforeComplete(validate: boolean): void {
    const { eventOperation } = this;

    if (!eventOperation) {
      return;
    }

    let { toDate, fromDate } = eventOperation;

    if (toDate < fromDate) {
      const dateSwap = toDate;

      toDate = fromDate;
      fromDate = dateSwap;
    }

    const isValid = validate
      ? this.dateDoesNotIntersect(fromDate, toDate, eventOperation.location) &&
        this.dateDoesNotIntersect(toDate, fromDate, eventOperation.location)
      : true;

    this.eventOperation = {
      ...eventOperation,

      toDate,
      fromDate,
      finished: true,

      isValid,
    };
  }

  #dateDoesNotIntersectExcluding(
    from: Date,
    to: Date,
    location: LocationType,
    excludeRecord: OccasionRecord,
  ): boolean {
    // a drag or resize arrives as a local midnight (`record.meta`), a half-cell
    // date (the old calendar) or a date shifted by `addDays` across a DST
    // change; compare whole canonical days, as the records are, so a same-day
    // turnover is never read as an overlap
    return (
      occupantsOf(location, canonicalDay(from), canonicalDay(to), [
        excludeRecord.model,
      ]).length === 0
    );
  }

  #swapForDrop({
    location,
    record,
    toDate,
    originalCheckIn,
    originalMultiUnit,
    createsConflict,
  }: IDragOperation): IUnitMove | undefined {
    if (
      this.isXCalendarRoute ||
      !createsConflict ||
      !toDate ||
      formatLocalDate(toDate) !== originalCheckIn ||
      !(location instanceof MultiUnitModel) ||
      !originalMultiUnit ||
      location === originalMultiUnit
    ) {
      return;
    }

    const move = this.unitMove.moveFor(
      record.model as ModelRegistry['booking'],
      location,
    );

    return move.swapWith ? move : undefined;
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/interaction': CalendarInteractionService;
  }
}
